import { useState, useCallback, useMemo, useRef, useEffect, type RefCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { APIError } from "../../lib/api";
import { StreamSidebar, type StreamMessage } from "../stream/StreamSidebar";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { OutlineNode, type OutlineNodeData } from "./OutlineNode";
import styles from "./OutlineEditor.module.css";

interface OutlineTemplate {
  id: string;
  icon: string;
  labelKey: string;
  sections: string[];
}

const OUTLINE_TEMPLATES: OutlineTemplate[] = [
  {
    id: "blog-post",
    icon: "📝",
    labelKey: "outline.templateBlogPost",
    sections: ["Introduction", "Key Points", "Supporting Details", "Conclusion"],
  },
  {
    id: "how-to",
    icon: "🔧",
    labelKey: "outline.templateHowTo",
    sections: ["Overview", "Prerequisites", "Step-by-Step", "Tips & Troubleshooting"],
  },
  {
    id: "essay",
    icon: "📖",
    labelKey: "outline.templateEssay",
    sections: ["Thesis", "Argument 1", "Argument 2", "Counterpoint", "Conclusion"],
  },
  {
    id: "newsletter",
    icon: "📬",
    labelKey: "outline.templateNewsletter",
    sections: ["Hook", "Main Story", "Quick Takes", "Call to Action"],
  },
];

export interface OutlineEditorProps {
  nodes: OutlineNodeData[];
  onChange: (nodes: OutlineNodeData[]) => void;
  onDeleteNode?: (nodeId: string) => void;
  onExpandPoint?: (nodeId: string, content: string) => Promise<string>;
  onGenerateFromStream?: () => void;
  onImportFromStream?: () => void;
  onNavigateToSettings?: () => void;
  isGenerating?: boolean;
  aiConfigured?: boolean | null;
  aiEnabled?: boolean;
  isZenMode?: boolean;
  projectId?: string;
  streamMessages?: StreamMessage[];
  isStreamSynced?: boolean;
  unsyncedStreamCount?: number;
}

export function OutlineEditor({
  nodes,
  onChange,
  onDeleteNode,
  onExpandPoint,
  onGenerateFromStream,
  onImportFromStream,
  onNavigateToSettings,
  isGenerating = false,
  aiConfigured,
  aiEnabled = true,
  isZenMode = false,
  projectId,
  streamMessages,
  isStreamSynced = false,
  unsyncedStreamCount = 0,
}: OutlineEditorProps) {
  const { t } = useTranslation();
  const hasStreamContent =
    streamMessages && streamMessages.some((m) => m.role === "user" && m.content.trim().length > 0);
  const [streamSidebarOpen, setStreamSidebarOpen] = useState(false);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [expandingNodeId, setExpandingNodeId] = useState<string | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => {
    if (!projectId) return new Set();
    try {
      const stored = localStorage.getItem(`outline_collapsed_${projectId}`);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  // Persist collapsed state to localStorage
  useEffect(() => {
    if (!projectId) return;
    if (collapsedIds.size === 0) {
      localStorage.removeItem(`outline_collapsed_${projectId}`);
    } else {
      localStorage.setItem(`outline_collapsed_${projectId}`, JSON.stringify([...collapsedIds]));
    }
  }, [collapsedIds, projectId]);

  // Keep a ref to the latest nodes so callbacks that fire between renders
  // (e.g. Enter-key handler on a freshly-seeded single-node outline) never
  // operate on a stale empty array.  Fixes #1235.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const pendingFocusIdRef = useRef<string | null>(null);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const inputRefsMap = useRef<Map<string, HTMLTextAreaElement>>(new Map());
  const pointerDragRef = useRef(false);

  const registerInputRef = useCallback((nodeId: string): RefCallback<HTMLTextAreaElement> => {
    return (el) => {
      if (el) {
        inputRefsMap.current.set(nodeId, el);
      } else {
        inputRefsMap.current.delete(nodeId);
      }
    };
  }, []);

  const focusNode = useCallback((nodeId: string) => {
    const el = inputRefsMap.current.get(nodeId);
    if (el) {
      el.focus();
    } else {
      // Node isn't in editing mode yet — request it to enter editing
      setEditingNodeId(nodeId);
    }
  }, []);

  // Custom collision detection that excludes the active (dragged) item from
  // candidates so the pointer doesn't resolve back to the item's own droppable.
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const filtered = args.droppableContainers.filter((c) => c.id !== args.active.id);
    return closestCenter({ ...args, droppableContainers: filtered });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const flattenedNodes = useMemo(() => {
    const flatten = (
      items: OutlineNodeData[],
      parentId: string | null = null,
      depth = 0,
    ): Array<{
      node: OutlineNodeData;
      parentId: string | null;
      depth: number;
    }> => {
      return items.reduce<Array<{ node: OutlineNodeData; parentId: string | null; depth: number }>>(
        (acc, item) => {
          acc.push({ node: item, parentId, depth });
          if (item.children && item.children.length > 0 && !collapsedIds.has(item.id)) {
            acc.push(...flatten(item.children, item.id, depth + 1));
          }
          return acc;
        },
        [],
      );
    };
    return flatten(nodes);
  }, [nodes, collapsedIds]);

  const nodeIds = useMemo(() => flattenedNodes.map((f) => f.node.id), [flattenedNodes]);

  const findNodeById = useCallback(
    (items: OutlineNodeData[], id: string): OutlineNodeData | null => {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findNodeById(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    [],
  );

  const updateNodeById = useCallback(
    (
      items: OutlineNodeData[],
      id: string,
      updates: Partial<OutlineNodeData>,
    ): OutlineNodeData[] => {
      return items.map((item) => {
        if (item.id === id) {
          return { ...item, ...updates };
        }
        if (item.children) {
          return {
            ...item,
            children: updateNodeById(item.children, id, updates),
          };
        }
        return item;
      });
    },
    [],
  );

  const deleteNodeById = useCallback((items: OutlineNodeData[], id: string): OutlineNodeData[] => {
    return items
      .filter((item) => item.id !== id)
      .map((item) => {
        if (!item.children) return item;
        const updatedChildren = deleteNodeById(item.children, id);
        return {
          ...item,
          children: updatedChildren.length > 0 ? updatedChildren : undefined,
        };
      });
  }, []);

  const addChildNode = useCallback(
    (items: OutlineNodeData[], parentId: string, newNode: OutlineNodeData): OutlineNodeData[] => {
      return items.map((item) => {
        if (item.id === parentId) {
          return {
            ...item,
            children: [...(item.children || []), newNode],
            collapsed: false,
          };
        }
        if (item.children) {
          return {
            ...item,
            children: addChildNode(item.children, parentId, newNode),
          };
        }
        return item;
      });
    },
    [],
  );

  const addSiblingNode = useCallback(
    (items: OutlineNodeData[], siblingId: string, newNode: OutlineNodeData): OutlineNodeData[] => {
      const result: OutlineNodeData[] = [];
      for (const item of items) {
        const updatedChildren = item.children
          ? addSiblingNode(item.children, siblingId, newNode)
          : undefined;
        const updatedItem =
          updatedChildren !== item.children ? { ...item, children: updatedChildren } : item;
        result.push(updatedItem);
        if (item.id === siblingId) {
          result.push(newNode);
        }
      }
      return result;
    },
    [],
  );

  const handleDragStart = (event: DragStartEvent) => {
    pointerDragRef.current =
      typeof PointerEvent !== "undefined" && event.activatorEvent instanceof PointerEvent;
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over ? (event.over.id as string) : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    // Blur the drag handle after pointer-initiated drags so it doesn't remain
    // in an active/focused state. Skip for keyboard-initiated drags to preserve
    // focus for continued keyboard navigation.
    if (
      typeof PointerEvent !== "undefined" &&
      event.activatorEvent instanceof PointerEvent &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }

    if (!over || active.id === over.id) return;

    const activeIdx = flattenedNodes.findIndex((f) => f.node.id === active.id);
    const overIdx = flattenedNodes.findIndex((f) => f.node.id === over.id);

    if (activeIdx === -1 || overIdx === -1) return;

    const activeFlat = flattenedNodes[activeIdx];
    const overFlat = flattenedNodes[overIdx];

    // Remove the active node from the tree
    let newNodes = deleteNodeById(nodesRef.current, activeFlat.node.id);

    // Determine where to insert: as a sibling at the same depth as the target.
    // If the target has a parent, insert into that parent's children; otherwise
    // insert at the top level.
    // When dragging DOWN (activeIdx < overIdx) we insert after the target;
    // when dragging UP (activeIdx > overIdx) we insert before the target.
    const insertAfter = activeIdx < overIdx;

    if (overFlat.parentId) {
      // Insert as a sibling inside the parent
      const insertIntoParent = (
        items: OutlineNodeData[],
        parentId: string,
        refId: string,
        nodeToInsert: OutlineNodeData,
      ): OutlineNodeData[] => {
        return items.map((item) => {
          if (item.id === parentId && item.children) {
            const idx = item.children.findIndex((c) => c.id === refId);
            const newChildren = [...item.children];
            newChildren.splice(insertAfter ? idx + 1 : idx, 0, nodeToInsert);
            return { ...item, children: newChildren };
          }
          if (item.children) {
            return {
              ...item,
              children: insertIntoParent(item.children, parentId, refId, nodeToInsert),
            };
          }
          return item;
        });
      };
      newNodes = insertIntoParent(newNodes, overFlat.parentId, over.id as string, activeFlat.node);
    } else {
      // Insert at top level
      const idx = newNodes.findIndex((n) => n.id === over.id);
      newNodes = [...newNodes];
      newNodes.splice(insertAfter ? idx + 1 : idx, 0, activeFlat.node);
    }

    onChange(newNodes);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);

    // Only blur for pointer-initiated drags to preserve keyboard focus
    if (pointerDragRef.current && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleUpdateNode = useCallback(
    (id: string, updates: Partial<OutlineNodeData>) => {
      onChange(updateNodeById(nodesRef.current, id, updates));
    },
    [onChange, updateNodeById],
  );

  const cleanUpCollapsedIds = useCallback((updated: OutlineNodeData[]) => {
    setCollapsedIds((prev) => {
      const orphaned = [...prev].filter((cid) => {
        const find = (items: OutlineNodeData[]): OutlineNodeData | null => {
          for (const item of items) {
            if (item.id === cid) return item;
            if (item.children) {
              const found = find(item.children);
              if (found) return found;
            }
          }
          return null;
        };
        const node = find(updated);
        return node && (!node.children || node.children.length === 0);
      });
      if (orphaned.length === 0) return prev;
      const next = new Set(prev);
      orphaned.forEach((cid) => next.delete(cid));
      return next;
    });
  }, []);

  const handleDeleteNode = useCallback(
    (id: string) => {
      const currentNodes = nodesRef.current;
      const updated = deleteNodeById(currentNodes, id);

      if (onDeleteNode) {
        // Skip collapsedIds cleanup here — the parent owns the API
        // lifecycle and will re-fetch on failure. Orphaned IDs in the
        // Set are inert (nodes that don't exist aren't rendered), so
        // skipping cleanup is safe. If we cleaned up eagerly and the
        // DELETE failed, the parent's rollback re-fetch would restore
        // nodes but collapsedIds would already be mutated, leaving
        // previously-collapsed parents permanently uncollapsed.
        onChange(updated);
        onDeleteNode(id);
      } else {
        cleanUpCollapsedIds(updated);
        const snapshot = currentNodes;
        onChange(updated);

        toast(
          (toastInstance) => (
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {t("outline.itemDeleted")}
              <button
                type="button"
                onClick={() => {
                  onChange(snapshot);
                  toast.dismiss(toastInstance.id);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--color-accent, #6366f1)",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                {t("outline.undo")}
              </button>
            </span>
          ),
          { duration: 5000 },
        );
      }
    },
    [onChange, deleteNodeById, onDeleteNode, cleanUpCollapsedIds, t],
  );

  const handleAddChild = useCallback(
    (parentId: string) => {
      const newNode: OutlineNodeData = {
        id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: "",
        isAiGenerated: false,
      };
      pendingFocusIdRef.current = newNode.id;
      // Auto-expand the parent so the new child is visible immediately
      setCollapsedIds((prev) => {
        if (!prev.has(parentId)) return prev;
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
      onChange(addChildNode(nodesRef.current, parentId, newNode));
    },
    [onChange, addChildNode],
  );

  const handleAddSibling = useCallback(
    (siblingId: string, pendingUpdates?: Partial<OutlineNodeData>) => {
      const newNode: OutlineNodeData = {
        id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        content: "",
        isAiGenerated: false,
      };
      pendingFocusIdRef.current = newNode.id;
      // Read from the ref so this callback always sees the latest nodes,
      // even if a React render hasn't flushed a new useCallback yet.
      let base = nodesRef.current;
      if (pendingUpdates) {
        base = updateNodeById(base, siblingId, pendingUpdates);
      }
      onChange(addSiblingNode(base, siblingId, newNode));
    },
    [onChange, addSiblingNode, updateNodeById],
  );

  const handleExpandPoint = useCallback(
    async (id: string) => {
      if (!onExpandPoint) return;

      const node = findNodeById(nodesRef.current, id);
      if (!node || !node.content.trim()) return;

      setExpandingNodeId(id);
      try {
        const expandedContent = await onExpandPoint(id, node.content);

        // Add the expanded content as a child note
        const newNode: OutlineNodeData = {
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          content: expandedContent,
          isAiGenerated: true,
          note: "AI-expanded from parent point",
        };
        // Auto-expand the parent so AI-generated child is visible
        setCollapsedIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        onChange(addChildNode(nodesRef.current, id, newNode));
      } catch (err) {
        console.error("Failed to expand point:", err);
        const code = err instanceof APIError ? (err.data as { code?: string })?.code : undefined;
        if (code === "AI_NOT_CONFIGURED" || code === "AI_AUTH_ERROR") {
          toast.error(
            (t) => (
              <span>
                AI is not configured.{" "}
                {onNavigateToSettings ? (
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      onNavigateToSettings();
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-primary, #3b82f6)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      padding: 0,
                      font: "inherit",
                    }}
                  >
                    Open Settings
                  </button>
                ) : (
                  "Add an API key in Settings to use this feature."
                )}
              </span>
            ),
            { id: "ai-config-error" },
          );
        } else {
          toast.error("Failed to expand point. Please try again.", {
            id: "ai-error",
          });
        }
      } finally {
        setExpandingNodeId(null);
      }
    },
    [onChange, onExpandPoint, findNodeById, addChildNode],
  );

  const handleToggleCollapse = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleIndentNode = useCallback(
    (id: string) => {
      // Find the node's position among its siblings and make it a child of the previous sibling
      const indentInList = (items: OutlineNodeData[]): OutlineNodeData[] | null => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === id) {
            if (i === 0) return null; // No previous sibling to indent under
            const prevSibling = items[i - 1];
            const nodeToMove = items[i];
            const newItems = [...items];
            newItems.splice(i, 1);
            newItems[i - 1] = {
              ...prevSibling,
              children: [...(prevSibling.children || []), nodeToMove],
            };
            // Uncollapse the new parent so the moved node is visible
            setCollapsedIds((prev) => {
              const next = new Set(prev);
              next.delete(prevSibling.id);
              return next;
            });
            return newItems;
          }
          if (items[i].children) {
            const result = indentInList(items[i].children!);
            if (result) {
              return items.map((item, idx) => (idx === i ? { ...item, children: result } : item));
            }
          }
        }
        return null;
      };
      const result = indentInList(nodesRef.current);
      if (result) onChange(result);
    },
    [onChange],
  );

  const handleOutdentNode = useCallback(
    (id: string) => {
      // Walk the tree looking for a parent whose child matches `id`.
      // When found, remove the node from that parent's children and insert
      // it after the parent in the grandparent's list.
      const outdent = (items: OutlineNodeData[]): OutlineNodeData[] | null => {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (!item.children) continue;

          const childIdx = item.children.findIndex((c) => c.id === id);
          if (childIdx !== -1) {
            // Found: item is the parent, item.children[childIdx] is the node to outdent
            const nodeToMove = item.children[childIdx];
            const newChildren = [...item.children];
            newChildren.splice(childIdx, 1);
            const updatedParent = {
              ...item,
              children: newChildren.length > 0 ? newChildren : undefined,
            };
            const result = [...items];
            result[i] = updatedParent;
            result.splice(i + 1, 0, nodeToMove);
            return result;
          }

          // Recurse into children
          const childResult = outdent(item.children);
          if (childResult) {
            return items.map((it, idx) => (idx === i ? { ...it, children: childResult } : it));
          }
        }
        return null;
      };
      const result = outdent(nodesRef.current);
      if (result) onChange(result);
    },
    [onChange],
  );

  const handleMoveUp = useCallback(
    (id: string) => {
      const moveInList = (items: OutlineNodeData[]): OutlineNodeData[] | null => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === id) {
            if (i === 0) return null; // Already first
            const newItems = [...items];
            [newItems[i - 1], newItems[i]] = [newItems[i], newItems[i - 1]];
            return newItems;
          }
          if (items[i].children) {
            const result = moveInList(items[i].children!);
            if (result) {
              return items.map((item, idx) => (idx === i ? { ...item, children: result } : item));
            }
          }
        }
        return null;
      };
      const result = moveInList(nodesRef.current);
      if (result) onChange(result);
    },
    [onChange],
  );

  const handleMoveDown = useCallback(
    (id: string) => {
      const moveInList = (items: OutlineNodeData[]): OutlineNodeData[] | null => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === id) {
            if (i === items.length - 1) return null; // Already last
            const newItems = [...items];
            [newItems[i], newItems[i + 1]] = [newItems[i + 1], newItems[i]];
            return newItems;
          }
          if (items[i].children) {
            const result = moveInList(items[i].children!);
            if (result) {
              return items.map((item, idx) => (idx === i ? { ...item, children: result } : item));
            }
          }
        }
        return null;
      };
      const result = moveInList(nodesRef.current);
      if (result) onChange(result);
    },
    [onChange],
  );

  const handleAddTopLevel = () => {
    const newNode: OutlineNodeData = {
      id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      content: "",
      isAiGenerated: false,
    };
    onChange([...nodesRef.current, newNode]);
  };

  const handleApplyTemplate = useCallback(
    (template: OutlineTemplate) => {
      const newNodes: OutlineNodeData[] = template.sections.map((section, i) => ({
        id: `node-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        content: section,
        isAiGenerated: false,
      }));
      onChange(newNodes);
    },
    [onChange],
  );

  const activeNode = activeId ? findNodeById(nodes, activeId) : null;

  return (
    <div className={`${styles.wrapper} ${streamSidebarOpen ? styles.wrapperWithSidebar : ""}`}>
      <div className={styles.container}>
        {!isZenMode && (
          <header className={styles.header}>
            <div className={styles.headerTitle}>
              {isGenerating && <span className={styles.headerBadge}>Generating...</span>}
            </div>
            {nodes.length > 0 && (
              <div className={styles.headerActions}>
                {hasStreamContent && onImportFromStream && !isStreamSynced && (
                  <button
                    type="button"
                    className={styles.importButton}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onImportFromStream}
                    title={t("outline.importFromStream")}
                  >
                    <ImportIcon />
                    <span className={styles.buttonLabel}>
                      {unsyncedStreamCount > 0
                        ? t("outline.syncStreamWithCount", { count: unsyncedStreamCount })
                        : t("outline.importFromStream")}
                    </span>
                  </button>
                )}
                {isStreamSynced && hasStreamContent && (
                  <span className={styles.syncedIndicator} title={t("outline.streamSyncedTooltip")}>
                    <CheckIcon />
                    <span className={styles.buttonLabel}>{t("outline.streamSynced")}</span>
                  </span>
                )}
                {hasStreamContent && (
                  <button
                    type="button"
                    className={`${styles.streamToggleButton} ${streamSidebarOpen ? styles.streamToggleButtonActive : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setStreamSidebarOpen(!streamSidebarOpen)}
                    title={streamSidebarOpen ? t("outline.hideStream") : t("outline.showStream")}
                    aria-pressed={streamSidebarOpen}
                  >
                    <StreamPanelIcon />
                  </button>
                )}
              </div>
            )}
          </header>
        )}

        {nodes.length === 0 ? (
          <div className={styles.emptyState}>
            <OutlineIllustration className={styles.emptyIllustration} />
            <h3 className={styles.emptyTitle}>{t("outline.emptyTitle")}</h3>
            <p className={styles.emptyDescription}>
              {aiEnabled && aiConfigured !== true
                ? t("outline.emptyDescriptionNoAI")
                : t("outline.emptyDescriptionAI")}
            </p>

            {/* Primary CTA */}
            <button type="button" className={styles.emptyPrimaryCTA} onClick={handleAddTopLevel}>
              <PlusIcon />
              {t("outline.addFirstSection")}
            </button>

            {/* Secondary actions row */}
            <div className={styles.emptySecondaryRow}>
              {aiEnabled && aiConfigured === true && onGenerateFromStream && (
                <button
                  type="button"
                  className={styles.emptySecondaryAction}
                  onClick={onGenerateFromStream}
                  disabled={isGenerating}
                >
                  {isGenerating ? <LoaderIcon className={styles.spinner} /> : <SparklesIcon />}
                  {isGenerating ? t("outline.generating") : t("outline.generateFromStream")}
                </button>
              )}

              {hasStreamContent && onImportFromStream && (
                <button
                  type="button"
                  className={styles.emptySecondaryAction}
                  onClick={onImportFromStream}
                >
                  <ImportIcon />
                  {unsyncedStreamCount > 0
                    ? t("outline.syncStreamWithCount", { count: unsyncedStreamCount })
                    : t("outline.importFromStream")}
                </button>
              )}

              <button
                type="button"
                className={styles.emptySecondaryAction}
                onClick={() => setTemplatesExpanded((prev) => !prev)}
              >
                📋 {t("outline.startWithTemplate", "Use Template")}
              </button>

              {aiEnabled && aiConfigured !== true && onNavigateToSettings && (
                <button
                  type="button"
                  className={styles.emptySecondaryAction}
                  onClick={onNavigateToSettings}
                >
                  <SparklesIcon />
                  {t("outline.connectAI", "Connect AI")}
                </button>
              )}
            </div>

            {templatesExpanded && (
              <div className={styles.templateSection}>
                <div className={styles.templateGrid}>
                  {OUTLINE_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={styles.templateCard}
                      onClick={() => handleApplyTemplate(template)}
                    >
                      <span className={styles.templateIcon}>{template.icon}</span>
                      <span className={styles.templateName}>{t(template.labelKey)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={nodeIds} strategy={verticalListSortingStrategy}>
              <div className={styles.nodeList}>
                {flattenedNodes.map(({ node, depth }) => (
                  <OutlineNode
                    key={node.id}
                    node={node}
                    depth={depth}
                    isDropTarget={overId === node.id && activeId !== node.id}
                    onUpdate={handleUpdateNode}
                    onDelete={handleDeleteNode}
                    onAddChild={handleAddChild}
                    onAddSibling={handleAddSibling}
                    onExpandPoint={onExpandPoint ? handleExpandPoint : undefined}
                    onToggleCollapse={handleToggleCollapse}
                    onIndent={handleIndentNode}
                    onOutdent={handleOutdentNode}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    isCollapsed={collapsedIds.has(node.id)}
                    isExpanding={expandingNodeId === node.id}
                    isZenMode={isZenMode}
                    totalNodeCount={flattenedNodes.length}
                    pendingFocusIdRef={pendingFocusIdRef}
                    nodeIds={nodeIds}
                    registerInputRef={registerInputRef}
                    focusNode={focusNode}
                    editingNodeId={editingNodeId}
                    clearEditingNodeId={setEditingNodeId}
                  />
                ))}
                <button
                  type="button"
                  className={styles.addSectionButton}
                  onClick={handleAddTopLevel}
                  aria-label={t("outline.addSection", "Add section")}
                >
                  <PlusIcon />
                  <span className={styles.addSectionLabel}>
                    {t("outline.addSection", "Add section")}
                  </span>
                </button>
              </div>
            </SortableContext>

            <DragOverlay>
              {activeNode ? (
                <div className={styles.dragOverlay}>
                  <span className={styles.dragHandle}>
                    <GripIcon />
                  </span>
                  <span className={styles.dragContent}>
                    {activeNode.content || "Untitled section"}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
      {streamSidebarOpen && streamMessages && (
        <StreamSidebar messages={streamMessages} onClose={() => setStreamSidebarOpen(false)} />
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 8.5L6.5 12L13 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 10V12.667A1.334 1.334 0 0 1 12.667 14H3.333A1.334 1.334 0 0 1 2 12.667V10M4.667 6.667 8 10M8 10l3.333-3.333M8 10V2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StreamPanelIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 3V13M3 8H13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LoaderIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 1.5V4M8 12v2.5M3.4 3.4l1.8 1.8M10.8 10.8l1.8 1.8M1.5 8H4M12 8h2.5M3.4 12.6l1.8-1.8M10.8 5.2l1.8-1.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 1L9.5 5.5L14 7L9.5 8.5L8 13L6.5 8.5L2 7L6.5 5.5L8 1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OutlineIllustration({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="120"
      height="100"
      viewBox="0 0 120 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Document shape */}
      <rect
        x="20"
        y="8"
        width="80"
        height="84"
        rx="6"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.2"
      />
      {/* Outline lines — hierarchical indentation */}
      <circle cx="36" cy="30" r="3" fill="currentColor" opacity="0.5" />
      <rect x="44" y="28" width="40" height="4" rx="2" fill="currentColor" opacity="0.35" />
      <circle cx="44" cy="44" r="2.5" fill="currentColor" opacity="0.4" />
      <rect x="50" y="42" width="32" height="4" rx="2" fill="currentColor" opacity="0.25" />
      <circle cx="44" cy="56" r="2.5" fill="currentColor" opacity="0.4" />
      <rect x="50" y="54" width="28" height="4" rx="2" fill="currentColor" opacity="0.25" />
      <circle cx="36" cy="70" r="3" fill="currentColor" opacity="0.5" />
      <rect x="44" y="68" width="36" height="4" rx="2" fill="currentColor" opacity="0.35" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="5" cy="4" r="1.5" fill="currentColor" />
      <circle cx="11" cy="4" r="1.5" fill="currentColor" />
      <circle cx="5" cy="8" r="1.5" fill="currentColor" />
      <circle cx="11" cy="8" r="1.5" fill="currentColor" />
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="11" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

export type { OutlineNodeData };
